import { Injectable } from '@angular/core';
import { AppConfig } from './app-config.interface';

/**
 * Application configuration service.
 * Provides access to runtime configuration loaded from /assets/config.json.
 *
 * This service is initialized before the application starts via APP_INITIALIZER.
 * Configuration is loaded once at startup and cached for the lifetime of the application.
 *
 * @example
 * ```typescript
 * export class MyComponent {
 *   private config = inject(AppConfigService);
 *
 *   ngOnInit() {
 *     const apiUrl = this.config.apiBaseUrl;
 *     const env = this.config.environment;
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private _config!: AppConfig;

  /**
   * Sets the configuration.
   * This method is called by the APP_INITIALIZER during application startup.
   * Should not be called directly by application code.
   * @internal
   */
  public setConfig(config: AppConfig): void {
    this._config = config;
  }

  /**
   * Gets the complete configuration object
   */
  public getConfig(): AppConfig {
    return this._config;
  }

  /**
   * Gets the API base URL
   */
  public get apiBaseUrl(): string {
    return this._config.apiBaseUrl;
  }

  /**
   * Gets the API version
   */
  public get apiVersion(): string {
    return this._config.apiVersion;
  }

  /**
   * Gets the API timeout in milliseconds
   */
  public get apiTimeout(): number {
    return this._config.apiTimeout ?? 30000;
  }

  /**
   * Gets the versioned API URL (baseUrl + version)
   * Example: "/api/v1" or "https://api.example.com/v1"
   */
  public get versionedApiUrl(): string {
    return `${this._config.apiBaseUrl}/${this._config.apiVersion}`;
  }

  /**
   * Gets the environment name
   */
  public get environment(): AppConfig['environment'] {
    return this._config.environment ?? 'local';
  }

  /**
   * Checks if running in local environment
   */
  public isLocal(): boolean {
    return this.environment === 'local';
  }

  /**
   * Checks if running in development environment
   */
  public isDevelopment(): boolean {
    return this.environment === 'dev';
  }

  /**
   * Checks if running in QA environment
   */
  public isQA(): boolean {
    return this.environment === 'qa';
  }

  /**
   * Checks if running in PR environment
   */
  public isPR(): boolean {
    return this.environment === 'pr';
  }

  /**
   * Checks if running in staging environment
   */
  public isStaging(): boolean {
    return this.environment === 'staging';
  }

  /**
   * Checks if running in production environment
   */
  public isProduction(): boolean {
    return this.environment === 'prod';
  }

  /**
   * Gets the top navbar bundle URL
   */
  public get topNavbarUrl(): string {
    return this._config.topNavbarUrl;
  }

  /**
   * Gets the product code
   */
  public get productCode(): string {
    return this._config?.productCode ?? 'HCMI';
  }

  /**
   * Gets the help URL for the navbar help icon
   */
  public get helpUrl(): string {
    return this._config?.helpUrl ?? 'https://success.neogov.com/';
  }

  /**
   * Gets the app switcher base URL.
   * Derives from environment if not explicitly configured.
   */
  public get appSwitcherBaseUrl(): string {
    if (this._config?.appSwitcherBaseUrl) {
      return this._config.appSwitcherBaseUrl;
    }
    // Derive from environment name (same pattern as accounts URL)
    const env = this._config?.environment;
    if (!env || env === 'local') return '';
    if (env === 'prod') return 'https://accounts.neogov.com';
    return `https://accounts.${env}.neogov.net`;
  }

  /**
   * Gets the OIDC authority URL
   */
  public get oidcAuthority(): string {
    return this._config?.oidcAuthority ?? 'https://accounts.neogov.com';
  }

  /**
   * Gets the login path for post-logout redirect.
   * Falls back to '/local-login' if not configured.
   */
  public get loginPath(): string {
    return this._config?.loginPath ?? '/local-login';
  }
}
